// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { applyMiddleware, createStore } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { rootReducer, SagaSlice } from 'saga-slice';
import { TypedUseSelectorHook, useSelector } from 'react-redux';
import { composeWithDevTools } from 'redux-devtools-extension/logOnlyInProduction';
import { all, fork } from 'redux-saga/effects';

// Bring in all of your saga slices together in whatever file
// you're going to declare your redux store
import AppSlice, { AppState, initialSaga } from './app/slice';
import ProjectsSlice from './projects/slice';
import SelectDataSlice from './selectData/slice';
import NotificationSlice from './notifications/slice';
import AdminUsersSlice from './users/slice';
import UserDataSlice from './userData/slice';
import CurrencySlice from './currency/slice';
import ApprovalSlice from './approval/slice';
import { NotificationState } from './notifications/stateType';
import { AdminUsersState } from './users/stateType';
import { UserDataState } from './userData/stateType';
import { CurrencyState } from './currency/stateType';
import { ProjectsState } from './projects/stateType';
import { SelectDataState } from './selectData/stateType';
import { ModalsState } from './modals/stateType';
import { ApprovalState } from './approval/stateType';
import ModalsSlice from './modals/slice';
import ArchiveSlice from './archive/slice';
import { ArchiveState } from './archive/stateType';
import ChangeLogSlice from './changelog/slice';
import { ChangeLogState } from './changelog/stateType';
import AnalyticsSlice from './analytics/slice';
import { AnalyticsState } from './analytics/stateType';
import FinancialFramesSlice from './financialFrames/slice';
import { FinancialFramesState } from './financialFrames/stateType';

const LOG_LEVEL =
  (typeof window === 'undefined'
    ? parseInt(process.env.LOG_LEVEL)
    : window.__LOG_LEVEL__) || 0;

let rootSagaPromise = null;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logger = () => (next) => (action) => {
  if (LOG_LEVEL < 3) {
    return next(action);
  }
  return next(action);
};

const rootSaga = function (modules: SagaSlice[]) {
  return function* () {
    // TODO-PPM uncomment the line bellow
    yield fork(initialSaga, AppSlice);
    yield all(
      modules
        .map((slice) => slice.sagas)
        .reduce((a, c) => a.concat(c), [])
        .map((saga) => saga()),
    );
  };
};

const configureStore = (preloadedState = {}) => {
  const modules = [
    AppSlice,
    ProjectsSlice,
    SelectDataSlice,
    AdminUsersSlice,
    NotificationSlice,
    UserDataSlice,
    CurrencySlice,
    ModalsSlice,
    ApprovalSlice,
    ArchiveSlice,
    ChangeLogSlice,
    AnalyticsSlice,
    FinancialFramesSlice,
  ];
  const sagaMiddleware = createSagaMiddleware();

  // Typically redux middleware
  const middleware = composeWithDevTools(
    applyMiddleware(...[sagaMiddleware, logger]),
  );
  const store = createStore(rootReducer(modules), preloadedState, middleware);
  rootSagaPromise = sagaMiddleware.run(rootSaga(modules)).toPromise();

  return store;
};
export async function waitForSagasToFinish() {
  return rootSagaPromise;
}

export interface RootState {
  app: AppState;
  projects: ProjectsState;
  selectData: SelectDataState;
  usersDetails: AdminUsersState;
  notifications: NotificationState;
  userData: UserDataState;
  currency: CurrencyState;
  modals: ModalsState;
  approval: ApprovalState;
  archive: ArchiveState;
  changelog: ChangeLogState;
  analytics: AnalyticsState;
  financialFrames: FinancialFramesState;
}

export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;

export default configureStore;
